import React from "react";
import { Link } from "react-router-dom";
import DocumentSlot from "./DocumentSlot";
import { ACCENT, inputClass } from "./shared";

// Contributor release waiver (spec 2.3.12). On the Metadata & Rights step the artist either says the recording is a solo
// recording, or lists everyone else who contributed (producer, featured artist, session player...) and confirms a signed
// Contributor Release Waiver is held for each one. A copy of the signed waiver can be attached (optional). The template is
// on the Terms & Disclosures tab.
export const BLANK_CONTRIBUTOR = { name: "", role: "", email: "", release_held: false, document: null };
export const MAX_CONTRIBUTORS = 20;

export function contributorsFromTrack(t) {
  return (Array.isArray(t?.contributors) ? t.contributors : []).map((c) => ({
    name: c.name || "",
    role: c.role || "",
    email: c.email || "",
    release_held: !!c.release_held,
    document: c.document_url ? { url: c.document_url, name: "Signed waiver" } : null,
  }));
}

export function modeFromTrack(t) {
  if (t?.solo_recording) return "solo";
  if (Array.isArray(t?.contributors) && t.contributors.length) return "others";
  return "";
}

// The pieces of the save body that belong to the waiver. Nothing is sent until the artist has chosen.
export function contributorBody(mode, contributors) {
  if (mode === "solo") return { solo_recording: true };
  if (mode !== "others") return {};
  return {
    solo_recording: false,
    contributors: (contributors || [])
      .filter((c) => (c.name || "").trim())
      .map((c) => ({
        name: c.name.trim(),
        role: (c.role || "").trim() || undefined,
        email: (c.email || "").trim() || undefined,
        release_held: !!c.release_held,
        document_url: c.document?.url || undefined,
      })),
  };
}

// Problems to show before the artist can leave the step (the server checks again on submit).
export function contributorProblems(mode, contributors) {
  if (mode !== "solo" && mode !== "others") return ["Please tell us whether anyone else contributed to this recording."];
  if (mode === "solo") return [];
  const list = (contributors || []).filter((c) => (c.name || "").trim() || (c.role || "").trim() || (c.email || "").trim());
  if (!list.length) return ["Add each contributor, or choose “Solo recording”."];
  const out = [];
  if (list.some((c) => !(c.name || "").trim())) out.push("Enter a name for every contributor.");
  if (list.some((c) => (c.email || "").trim() && !/^\S+@\S+\.\S+$/.test(c.email.trim()))) out.push("Enter a valid email for each contributor, or leave it empty.");
  if (list.some((c) => !c.release_held)) out.push("Every contributor needs a signed release. Tick the box for each person once you hold it.");
  return out;
}

const ModeOption = ({ value, current, onChange, title, text, disabled }) => (
  <label className={`block rounded-xl border px-4 py-3 cursor-pointer ${current === value ? "border-2 border-gray-900" : "border-gray-200"}`}>
    <input type="radio" name="contributor-mode" value={value} className="sr-only" aria-label={title} checked={current === value} onChange={() => onChange(value)} disabled={disabled} />
    <span className="block font-bold text-gray-900">{title}</span>
    <span className="block text-sm text-gray-500">{text}</span>
  </label>
);

export default function ContributorReleases({ mode, onModeChange, contributors, onChange, disabled = false, error = "" }) {
  const update = (index, changes) => onChange(contributors.map((c, i) => (i === index ? { ...c, ...changes } : c)));
  const remove = (index) => onChange(contributors.filter((_, i) => i !== index));
  const choose = (value) => {
    onModeChange(value);
    if (value === "others" && !contributors.length) onChange([{ ...BLANK_CONTRIBUTOR }]);
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-[#faf9f7] px-4 py-4" data-testid="contributor-releases">
      <h3 className="font-bold text-gray-900">Contributor release waiver</h3>
      <p className="mt-1 text-sm text-gray-600">
        Anyone who played, wrote, produced or featured on this recording must release their rights to you in writing.{" "}
        <Link to="/artist-studio/rights/terms" target="_blank" className="font-semibold underline" style={{ color: ACCENT }}>Get the waiver template</Link>
      </p>

      <fieldset disabled={disabled} className="mt-3 space-y-2 min-w-0">
        <ModeOption value="solo" current={mode} onChange={choose} disabled={disabled} title="Solo recording" text="I am the only contributor. Nobody else needs to sign a release." />
        <ModeOption value="others" current={mode} onChange={choose} disabled={disabled} title="Other contributors" text="List each person and confirm you hold their signed release." />
      </fieldset>

      {mode === "others" && (
        <div className="mt-4 space-y-4">
          {contributors.map((c, i) => (
            <div key={i} className="rounded-xl border border-gray-200 bg-white p-4" data-testid={`contributor-${i}`}>
              <div className="grid gap-3 sm:grid-cols-3">
                <label className="block text-sm font-semibold text-gray-800">Name
                  <input className={`${inputClass(false)} mt-1`} value={c.name} maxLength={120} disabled={disabled} aria-label={`Contributor ${i + 1} name`} onChange={(e) => update(i, { name: e.target.value })} />
                </label>
                <label className="block text-sm font-semibold text-gray-800">Role
                  <input className={`${inputClass(false)} mt-1`} value={c.role} maxLength={60} placeholder="Producer, featured artist…" disabled={disabled} aria-label={`Contributor ${i + 1} role`} onChange={(e) => update(i, { role: e.target.value })} />
                </label>
                <label className="block text-sm font-semibold text-gray-800">Email (optional)
                  <input className={`${inputClass(false)} mt-1`} type="email" value={c.email} maxLength={190} disabled={disabled} aria-label={`Contributor ${i + 1} email`} onChange={(e) => update(i, { email: e.target.value })} />
                </label>
              </div>
              <label className="mt-3 flex items-start gap-2 text-sm text-gray-800">
                <input type="checkbox" className="mt-1" checked={c.release_held} disabled={disabled} aria-label={`Signed release held for contributor ${i + 1}`} onChange={(e) => update(i, { release_held: e.target.checked })} />
                <span>I hold a signed Contributor Release Waiver from this person.</span>
              </label>
              <div className="mt-3">
                <DocumentSlot label="Signed waiver (optional)" value={c.document} onChange={(f) => update(i, { document: f })} disabled={disabled} />
              </div>
              {contributors.length > 1 && !disabled && (
                <button type="button" onClick={() => remove(i)} className="mt-3 text-sm font-semibold text-red-600 underline" aria-label={`Remove contributor ${i + 1}`}>Remove</button>
              )}
            </div>
          ))}
          {contributors.length < MAX_CONTRIBUTORS && !disabled && (
            <button type="button" onClick={() => onChange([...contributors, { ...BLANK_CONTRIBUTOR }])} className="text-sm font-semibold underline" style={{ color: ACCENT }}>+ Add another contributor</button>
          )}
        </div>
      )}
      {error && <p className="mt-3 text-sm text-red-600" role="alert">{error}</p>}
    </div>
  );
}
